const htmlMarkerRegex = /^\s*<!DOCTYPE\s|^\s*<html[\s>]/i;
const titleRegex = /<title[^>]*>([\s\S]*?)<\/title>/i;
const cfRayRegex = /Cloudflare Ray ID:\s*<strong[^>]*>\s*([a-f0-9]+)\s*<\/strong>/i;
const cfStatusRegex = />\s*(\d{3})\s*<\/h/i;

/**
 * @typedef {object} StatusHandler
 * @property {string} errorClass
 * @property {string} message
 * @property {boolean} [report=true] - When false, `throwIfHtmlResponse` will silently return instead of throwing for this status code
 *
 * @type {Record<number, StatusHandler>}
 */
const statusHandlers = {
  403: {
    errorClass: 'Request Forbidden',
    message: 'Request blocked by firewall',
    report: false
  },
  429: {
    errorClass: 'Request Rate Limit Exceeded',
    message: 'Request rate limit exceeded'
  }
};

/**
 * Attempts to extract a structured error descriptor from an HTML response body.
 *
 * @param {string} html - Raw HTML response body.
 * @returns {{ statusCode: number|null, source: string, title: string, message: string, rayId: string|null }}
 */
function parseHtmlError(html) {
  const titleMatch = html.match(titleRegex);
  const title = titleMatch ? titleMatch[1].trim() : '';
  const isCloudflare =
    /cloudflare/i.test(title) || /cloudflare/i.test(html.slice(0, 4000));

  let statusCode = null;
  let source = 'unknown';
  let message = 'Received unexpected HTML response';
  let rayId = null;

  if (isCloudflare) {
    source = 'cloudflare';
    const rayMatch = html.match(cfRayRegex);
    rayId = rayMatch ? rayMatch[1] : null;

    if (
      /you have been blocked/i.test(html) ||
      /attention required/i.test(title)
    ) {
      statusCode = 403;
    } else if (/you are being rate limited/i.test(html)) {
      statusCode = 429;
    } else {
      const statusMatch = title.match(/\b([45]\d{2})\b/);
      statusCode = statusMatch ? parseInt(statusMatch[1], 10) : null;
    }

    if (!statusCode) {
      const bodyStatusMatch = html.match(cfStatusRegex);
      statusCode = bodyStatusMatch ? parseInt(bodyStatusMatch[1], 10) : null;
    }

    const handler = statusCode ? statusHandlers[statusCode] : null;
    message = handler
      ? handler.message
      : `Cloudflare error${statusCode ? ` (${statusCode})` : ''}`;
  } else {
    const statusMatch = title.match(/\b([45]\d{2})\b/);
    statusCode = statusMatch ? parseInt(statusMatch[1], 10) : null;
  }

  return { statusCode, source, title, message, rayId };
}

class HttpResponseError extends Error {
  /**
   * @param {string} message
   * @param {{ statusCode?: number|null, source?: string, title?: string, rayId?: string|null }} details
   */
  constructor(
    message,
    { statusCode = null, source = 'unknown', title = '', rayId = null } = {}
  ) {
    super(message);
    this.name = 'HttpResponseError';
    this.statusCode = statusCode;
    this.source = source;
    this.title = title;
    this.rayId = rayId;
  }
}

/**
 * Returns true when the value looks like an HTML document body.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
function isHtmlResponse(value) {
  return typeof value === 'string' && htmlMarkerRegex.test(value);
}

/**
 * If `value` is an HTML string, parses it and throws an HttpResponseError
 * unless the matched status code has `report: false` in statusHandlers.
 *
 * @param {unknown} value
 * @returns {boolean} `true` if the response was HTML but suppressed, `false` otherwise.
 * @throws {HttpResponseError}
 */
function throwIfHtmlResponse(value) {
  if (!isHtmlResponse(value)) {
    return false;
  }

  const parsed = parseHtmlError(value);
  const handler = parsed.statusCode ? statusHandlers[parsed.statusCode] : null;

  if (handler && handler.report === false) {
    return true;
  }

  throw new HttpResponseError(parsed.message, parsed);
}

export {
  HttpResponseError,
  statusHandlers,
  parseHtmlError,
  isHtmlResponse,
  throwIfHtmlResponse
};
